/**
 * @fileoverview Orchard (RedPallas) FROST engine bridge.
 *
 * A framework-agnostic port of WINBIT32's `orchardFrostBridge` trimmed to the
 * pieces Secresea's initiator needs: load the WASM, derive a UFVK / unified
 * address from a FROST share, and (for tests / local 2-of-2) split a key with
 * a trusted dealer. The two-round signing ceremony wrappers are added in the
 * relay phase so their wire shapes stay matched to the protocol.
 *
 * Design for reuse: there is no module-global "current WASM" the way the
 * original bridge keeps one. The loaded module is returned and passed
 * explicitly into each function (dependency injection), so a host can run
 * more than one engine instance and nothing leaks across React renders.
 * The only internal state is a load cache keyed by asset URL.
 *
 * The underlying WASM is `orchard-frost-wasm` (built from frosty-lib /
 * orchard-frost-wasm), served as static assets. See {@link CosignConfig}.
 */

import type { CosignConfig } from './config';

// ─── WASM module surface (subset of orchard-frost-wasm we rely on) ──

export interface OrchardFrostWasmModule {
	/** Trusted-dealer split: returns JSON {shares, public_key_package}. */
	keygen_with_dealer: (max_signers: number, min_signers: number) => string;
	/** Round 1: opaque nonces + serialisable commitments for one signer. */
	commit_nonces: (secret_share_hex: string) => string;
	/** Round 2: a signature share, bound to the per-action randomizer. */
	sign_with_nonces: (
		identifier_hex: string,
		secret_share_hex: string,
		public_hex: string,
		group_public_hex: string,
		nonces_opaque_hex: string,
		all_commitments_json: string,
		message_hex: string,
		randomizer_hex: string,
	) => string;
	/** Aggregate signature shares into a 64-byte RedPallas signature. */
	aggregate: (
		commitments_json: string,
		message_hex: string,
		sig_shares_json: string,
		signer_pubkeys_json: string,
		group_public_hex: string,
		randomizer_hex: string,
	) => string;
	generate_randomizer: () => string;
	get_group_verifying_key: (group_public_hex: string) => string;
	/** Build an Orchard-only UFVK whose `ak` == the FROST group_public. */
	build_orchard_ufvk_from_frost?: (group_public_hex: string, orchard_extras_hex: string) => string;
	build_orchard_unified_address_from_frost?: (group_public_hex: string, orchard_extras_hex: string) => string;
	/** Random 64-byte nk‖rivk extras compatible with a given group_public. */
	orchard_random_extras?: (group_public_hex: string) => string;
	/** wasm-bindgen (`--target web`) init entry point. */
	default: (input: BufferSource | string | URL | Request | Response) => Promise<unknown>;
}

// ─── Key material shapes (mirror the `.wult` orchard bundle) ────────

export interface OrchardKeyShare {
	id: string;
	secretShare: string;
	publicKey: string;
	groupPublic: string;
}

export interface OrchardPublicKeyPackage {
	signerPubkeys: Record<string, string>;
	groupPublic: string;
}

export interface OrchardKeyBundle {
	myShare: OrchardKeyShare;
	publicKeyPackage: OrchardPublicKeyPackage;
	/** 64-byte nk‖rivk that, with the group_public (ak), forms the FVK. */
	orchardExtras?: string;
	minSigners?: number;
	maxSigners?: number;
	birthday?: number;
	[key: string]: unknown;
}

export interface OrchardKeygenResult {
	shares: Record<string, OrchardKeyShare>;
	publicKeyPackage: OrchardPublicKeyPackage;
}

export interface OrchardKeygenWithExtras {
	keygen: OrchardKeygenResult;
	orchardExtras: string;
	keygenAttempts: number;
}

export interface OrchardAddressResult {
	unifiedAddress: string;
	ufvk: string;
}

/** Attempts before giving up on producing an Orchard-compatible `ak`. */
export const DEFAULT_KEYGEN_RETRY_LIMIT = 32;

// ─── WASM loader (config-injected, cached per asset URL) ────────────

const wasmCache = new Map<string, Promise<OrchardFrostWasmModule>>();

const cacheBustSuffix = (config: CosignConfig): string =>
	config.assetCacheBustToken ? `?v=${encodeURIComponent(config.assetCacheBustToken)}` : '';

/**
 * Load + initialise the Orchard FROST WASM.
 *
 * Uses `config.loadOrchardFrostWasm` when provided (hosts that bundle the WASM
 * themselves). Otherwise lazily fetches the artefacts from `config.wasmBaseUrl`
 * — the dynamic `import()` is deliberate: it keeps the ~500 KB engine off the
 * critical path until a co-signing flow actually needs it.
 */
export const loadOrchardFrostWasm = (config: CosignConfig): Promise<OrchardFrostWasmModule> => {
	if (config.loadOrchardFrostWasm) {
		return config.loadOrchardFrostWasm();
	}

	const jsUrl = `${config.wasmBaseUrl}/orchard_frost_wasm.js${cacheBustSuffix(config)}`;
	const wasmUrl = `${config.wasmBaseUrl}/orchard_frost_wasm_bg.wasm${cacheBustSuffix(config)}`;

	const cached = wasmCache.get(jsUrl);
	if (cached) return cached;

	const loading = (async (): Promise<OrchardFrostWasmModule> => {
		try {
			const glue = (await import(/* @vite-ignore */ jsUrl)) as unknown as OrchardFrostWasmModule;
			const bytes = await config.fetchImpl(wasmUrl).then((r) => r.arrayBuffer());
			await glue.default(bytes);
			return glue;
		} catch (err) {
			wasmCache.delete(jsUrl);
			throw new Error(
				`Failed to initialise orchard-frost WASM from ${config.wasmBaseUrl}: ${(err as Error).message}`,
			);
		}
	})();

	wasmCache.set(jsUrl, loading);
	return loading;
};

// ─── Engine operations (WASM module injected) ───────────────────────

/** Trusted-dealer t-of-n split (used by tests and local 2-of-2 setups). */
export const orchardKeygenWithDealer = (
	wasm: OrchardFrostWasmModule,
	maxSigners: number,
	minSigners: number,
): OrchardKeygenResult => {
	const result = JSON.parse(wasm.keygen_with_dealer(maxSigners, minSigners));
	const shares: Record<string, OrchardKeyShare> = {};
	for (const [idHex, share] of Object.entries(result.shares as Record<string, any>)) {
		shares[idHex] = {
			id: share.id,
			secretShare: share.secret_share,
			publicKey: share.public,
			groupPublic: share.group_public,
		};
	}
	return {
		shares,
		publicKeyPackage: {
			signerPubkeys: result.public_key_package.signer_pubkeys,
			groupPublic: result.public_key_package.group_public,
		},
	};
};

/** Generate random 64-byte nk‖rivk extras compatible with `groupPublicHex`. */
export const generateOrchardExtras = (wasm: OrchardFrostWasmModule, groupPublicHex: string): string => {
	if (!wasm.orchard_random_extras) {
		throw new Error('orchard-frost WASM lacks orchard_random_extras — rebuild/redeploy the WASM bundle.');
	}
	return wasm.orchard_random_extras(groupPublicHex);
};

/**
 * Whether a 32-byte serialised group_public is a usable Orchard `ak`.
 *
 * Orchard's `SpendValidatingKey::from_bytes` rejects points whose y-sign bit
 * (top bit of byte 31) is set, and the identity (all-zero) point. RedPallas
 * keygen produces a random y-sign, so ~half of keygens are unusable as-is.
 */
export const isOrchardCompatibleGroupPublicHex = (groupPublicHex: string): boolean => {
	if (typeof groupPublicHex !== 'string' || groupPublicHex.length !== 64) return false;
	const lastByte = parseInt(groupPublicHex.slice(62, 64), 16);
	if (Number.isNaN(lastByte)) return false;
	if ((lastByte & 0x80) !== 0) return false;
	if (/^0+$/.test(groupPublicHex)) return false;
	return true;
};

/**
 * Trusted-dealer keygen that re-rolls until the group_public is an
 * Orchard-compatible `ak`, then derives matching nk‖rivk extras. With
 * p ≈ 0.5 per attempt, the default 32-attempt cap fails with probability
 * ≈ 2^-32. Returns the keygen, the extras, and how many attempts it took.
 */
export const orchardKeygenWithCompatibleExtras = (
	wasm: OrchardFrostWasmModule,
	maxSigners: number,
	minSigners: number,
	options: { maxAttempts?: number } = {},
): OrchardKeygenWithExtras => {
	if (!wasm.orchard_random_extras) {
		throw new Error('orchard-frost WASM lacks orchard_random_extras — rebuild/redeploy the WASM bundle.');
	}
	const maxAttempts = Math.max(1, options.maxAttempts ?? DEFAULT_KEYGEN_RETRY_LIMIT);
	let lastError: unknown = null;
	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		const keygen = orchardKeygenWithDealer(wasm, maxSigners, minSigners);
		const groupPublic = keygen.publicKeyPackage.groupPublic;
		if (!isOrchardCompatibleGroupPublicHex(groupPublic)) {
			lastError = new Error(`group_public has wrong y-sign (attempt ${attempt})`);
			continue;
		}
		try {
			return { keygen, orchardExtras: wasm.orchard_random_extras(groupPublic), keygenAttempts: attempt };
		} catch (err) {
			lastError = err;
		}
	}
	const detail = lastError instanceof Error ? lastError.message : String(lastError ?? 'unknown error');
	throw new Error(`orchard-frost keygen could not produce an Orchard-compatible ak after ${maxAttempts} attempts: ${detail}`);
};

/**
 * Derive the Orchard UFVK + unified address for a FROST share bundle.
 *
 * The group_public is already the 32-byte serialised `ak`, so `ak‖nk‖rivk`
 * trivially yields the Orchard FVK the PCZT will embed and verify against.
 * Backfills missing `orchardExtras` (mutating the bundle so the caller can
 * persist it) — without them the derived UFVK would not match the FROST
 * signer's `ak` and every shielded send would fail.
 *
 * @returns the address pair, or null if extras can't be produced / the WASM
 *   lacks the FROST-native helpers.
 */
export const deriveOrchardAddressFromBundle = (
	wasm: OrchardFrostWasmModule,
	bundle: OrchardKeyBundle,
): OrchardAddressResult | null => {
	const groupPublicHex = bundle.publicKeyPackage.groupPublic;

	if (!bundle.orchardExtras) {
		try {
			bundle.orchardExtras = generateOrchardExtras(wasm, groupPublicHex);
		} catch (err) {
			console.error('[cosign/orchard] could not backfill orchardExtras:', (err as Error).message);
			return null;
		}
	}

	if (!wasm.build_orchard_ufvk_from_frost || !wasm.build_orchard_unified_address_from_frost) {
		console.error('[cosign/orchard] WASM lacks build_orchard_*_from_frost — rebuild/redeploy.');
		return null;
	}

	try {
		const ufvk = wasm.build_orchard_ufvk_from_frost(groupPublicHex, bundle.orchardExtras);
		const unifiedAddress = wasm.build_orchard_unified_address_from_frost(groupPublicHex, bundle.orchardExtras);
		return { unifiedAddress, ufvk };
	} catch (err) {
		console.error('[cosign/orchard] FROST-native address derivation failed:', (err as Error).message);
		return null;
	}
};

// ─── Two-round signing ceremony primitives ──────────────────────────
//
// Thin, faithful wrappers over the WASM. Shapes match WINBIT32's
// orchardFrostBridge so the existing cosigner interoperates unchanged.

export interface NonceCommitment {
	/** Opaque, Rust-owned nonce handle — never leaves this device. */
	noncesOpaque: string;
	/** Serialisable per-signer commitments, shared with the other party. */
	commitments: string;
}

/** Round 1: produce this signer's nonces (opaque) + commitments (shareable). */
export const orchardCommitNonces = (wasm: OrchardFrostWasmModule, secretShareHex: string): NonceCommitment => {
	const result = JSON.parse(wasm.commit_nonces(secretShareHex));
	return { noncesOpaque: result.nonces_opaque, commitments: result.commitments };
};

/**
 * Round 2: produce this signer's signature share using its pre-committed
 * nonces. Both parties MUST pass the same `allCommitmentsJson`, `messageHex`
 * (sighash) and `randomizerHex` (the PCZT's per-action `alpha`).
 */
export const orchardSignWithNonces = (
	wasm: OrchardFrostWasmModule,
	identifierHex: string,
	secretShareHex: string,
	publicHex: string,
	groupPublicHex: string,
	noncesOpaqueHex: string,
	allCommitmentsJson: string,
	messageHex: string,
	randomizerHex: string,
): string =>
	wasm.sign_with_nonces(
		identifierHex,
		secretShareHex,
		publicHex,
		groupPublicHex,
		noncesOpaqueHex,
		allCommitmentsJson,
		messageHex,
		randomizerHex,
	);

/**
 * Aggregate signature shares into a 64-byte RedPallas signature.
 * Returns the raw WASM JSON (`{ signature, ... }`); callers read `.signature`.
 */
export const orchardAggregate = (
	wasm: OrchardFrostWasmModule,
	commitmentsJson: string,
	messageHex: string,
	sigSharesJson: string,
	signerPubkeysJson: string,
	groupPublicHex: string,
	randomizerHex: string,
): string =>
	wasm.aggregate(commitmentsJson, messageHex, sigSharesJson, signerPubkeysJson, groupPublicHex, randomizerHex);

/** Generate a random 32-byte scalar usable as a FROST randomizer (tests only — the live path uses the PCZT's alpha). */
export const orchardGenerateRandomizer = (wasm: OrchardFrostWasmModule): string => wasm.generate_randomizer();

/** Derive the (un-randomized) group verifying key from a group_public. */
export const orchardGroupVerifyingKey = (wasm: OrchardFrostWasmModule, groupPublicHex: string): string =>
	wasm.get_group_verifying_key(groupPublicHex);
